import {ToggleWishlistParams} from './types';
import {useAddToWishlist} from './useAddToWishlist';
import {useRemoveFromWishlist} from './useRemoveFromWishlist';

/**
 * Composite mutation hook for toggling wishlist items (add/remove)
 *
 * Uses useAddToWishlist and useRemoveFromWishlist internally.
 * Determines which action to take based on isInWishlist parameter.
 *
 * @returns Object with mutate function, isPending, isError, and error states
 *
 * @example
 * const {mutate: toggleWishlist, isPending} = useToggleWishlist();
 *
 * toggleWishlist({
 *   item: orderLine,
 *   isInWishlist: false // Will add to wishlist
 * });
 */
export const useToggleWishlist = () => {
  const addMutation = useAddToWishlist();
  const removeMutation = useRemoveFromWishlist();

  const mutate = ({item, isInWishlist}: ToggleWishlistParams) => {
    if (isInWishlist) {
      removeMutation.mutate(item);
    } else {
      addMutation.mutate(item);
    }
  };

  const mutateAsync = async ({item, isInWishlist}: ToggleWishlistParams) => {
    if (isInWishlist) {
      return removeMutation.mutateAsync(item);
    } else {
      return addMutation.mutateAsync(item);
    }
  };

  return {
    mutate,
    mutateAsync,
    isPending: addMutation.isPending || removeMutation.isPending,
    isError: addMutation.isError || removeMutation.isError,
    error: addMutation.error || removeMutation.error,
  };
};
